import { useEffect } from "react";
import { BACKEND_URL } from "@/lib/api";

/**
 * Hook: inject consultant-specific PWA manifest + apple-touch-icon + theme-color
 * for ALL pages under /c/:slug/* (Landing, Onboarding, Report, Profile, etc.).
 *
 * Why important:
 *   - When a client scans the QR, they first land on /c/:slug (which may immediately
 *     redirect to /c/:slug/onboarding if not yet registered).
 *   - The browser captures the manifest on initial page load to power "Add to Home Screen".
 *   - If the manifest still points to the generic GoStreet one, the installed PWA will
 *     open https://gostreet.it/ instead of /c/:slug, and use the generic icon.
 *
 * This hook ensures the manifest is always the consultant's one while the user is
 * on any /c/:slug/* route, and is restored when they leave.
 *
 * @param {string|null} slug - consultant slug from URL params
 * @param {object|null} consultant - consultant object (with optional logo_url, pwa_icon_url, brand_color_primary)
 */
export default function useConsultantPWAManifest(slug, consultant) {
  useEffect(() => {
    if (!slug) return;

    // 1) Inject manifest link pointing to the per-consultant manifest endpoint.
    //    We do this even before the consultant object loads — the endpoint resolves
    //    the slug on the backend.
    const manifestHref = `${BACKEND_URL}/api/consultants/by-slug/${slug}/manifest.json`;
    let manifestLink = document.querySelector('link[rel="manifest"]');
    let manifestWasInjected = false;
    if (manifestLink) {
      manifestLink.dataset.gsOriginalHref =
        manifestLink.dataset.gsOriginalHref || manifestLink.getAttribute("href") || "";
      manifestLink.setAttribute("href", manifestHref);
    } else {
      manifestLink = document.createElement("link");
      manifestLink.rel = "manifest";
      manifestLink.href = manifestHref;
      manifestLink.dataset.gsInjected = "1";
      document.head.appendChild(manifestLink);
      manifestWasInjected = true;
    }

    // 2) Apple-touch-icon + theme color — these require the consultant object to be loaded
    let appleIcon = null;
    let appleIconWasInjected = false;
    let themeColor = null;
    let themeColorWasInjected = false;

    if (consultant) {
      const iconUrl =
        consultant.pwa_icon_url
          ? `${BACKEND_URL}/api/files/${consultant.pwa_icon_url}`
          : consultant.logo_url
          ? `${BACKEND_URL}/api/files/${consultant.logo_url}`
          : null;

      if (iconUrl) {
        appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
        if (appleIcon) {
          appleIcon.dataset.gsOriginalHref =
            appleIcon.dataset.gsOriginalHref || appleIcon.getAttribute("href") || "";
          appleIcon.setAttribute("href", iconUrl);
        } else {
          appleIcon = document.createElement("link");
          appleIcon.rel = "apple-touch-icon";
          appleIcon.href = iconUrl;
          appleIcon.dataset.gsInjected = "1";
          document.head.appendChild(appleIcon);
          appleIconWasInjected = true;
        }
      }

      const brand = consultant.brand_color_primary || "#F77F2A";
      themeColor = document.querySelector('meta[name="theme-color"]');
      if (themeColor) {
        themeColor.dataset.gsOriginalContent =
          themeColor.dataset.gsOriginalContent || themeColor.getAttribute("content") || "";
        themeColor.setAttribute("content", brand);
      } else {
        themeColor = document.createElement("meta");
        themeColor.name = "theme-color";
        themeColor.content = brand;
        themeColor.dataset.gsInjected = "1";
        document.head.appendChild(themeColor);
        themeColorWasInjected = true;
      }
    }

    // 3) Update <title> so installed app shows nicer label
    const originalTitle = document.title;
    if (consultant?.studio_name) {
      document.title = `Gestione Sinistri · ${consultant.studio_name}`;
    }

    // Cleanup on unmount: restore originals
    return () => {
      if (manifestLink) {
        if (manifestWasInjected) {
          manifestLink.remove();
        } else if (manifestLink.dataset.gsOriginalHref) {
          manifestLink.setAttribute("href", manifestLink.dataset.gsOriginalHref);
        }
      }
      if (appleIcon) {
        if (appleIconWasInjected) {
          appleIcon.remove();
        } else if (appleIcon.dataset.gsOriginalHref) {
          appleIcon.setAttribute("href", appleIcon.dataset.gsOriginalHref);
        }
      }
      if (themeColor) {
        if (themeColorWasInjected) {
          themeColor.remove();
        } else if (themeColor.dataset.gsOriginalContent) {
          themeColor.setAttribute("content", themeColor.dataset.gsOriginalContent);
        }
      }
      document.title = originalTitle;
    };
  }, [slug, consultant]);
}
